import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import moment from 'moment-timezone';
import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/Dialog';
import {
  EditReservationFormI,
  ReservationGeneral,
  isMaintenanceBlockReservation
} from '../../../models/owner-portal/reservationGeneral';
import DatePickerForm from '../../dashboard/reservation/form/components/DatePickerForm';
import FormProvider from '../../../components/hook-form/FormProvider';
import { confirmActionT } from '../../dashboard/reservation/ReservationDetails';
import { useAuthContext } from '../../../auth/useAuthContext';
import {
  invalidateReservationCaches,
  usePutRTKQReservationCancelMutation,
  usePutRTKQReservationUpdateMutation
} from '../../../redux/rtkQuery/apiSlice';
import { useDispatch } from '../../../redux/store';
import { brandConfig } from '../../../config';
import { maintenanceBlockLabel } from '../../../utils/owner-portal/booking/bookingKinds';
import { classifyBookingError } from '../../../utils/owner-portal/booking/bookingErrors';
import { formatInstantInZone } from '../../../utils/owner-portal/booking/maintenanceTime';
import { useReservationPopoverState } from '../../dashboard/reservation/list/useReservationPopoverState';

type PopoverState = ReturnType<typeof useReservationPopoverState>;

export type ReservationOwnerDialogsContextValue = {
  handleOpenConfirm: (action: confirmActionT) => void;
  handleCloseConfirm: () => void;
  handleOpenCancelConfirmed: PopoverState['handleOpenCancelConfirmed'];
};

const ReservationOwnerDialogsContext = createContext<
  ReservationOwnerDialogsContextValue | undefined
>(undefined);

export function useReservationOwnerDialogsContext(): ReservationOwnerDialogsContextValue {
  const ctx = useContext(ReservationOwnerDialogsContext);
  if (!ctx) {
    throw new Error(
      'useReservationOwnerDialogsContext must be used within ReservationOwnerDialogsProvider'
    );
  }
  return ctx;
}

export function useOptionalReservationOwnerDialogsContext():
  | ReservationOwnerDialogsContextValue
  | undefined {
  return useContext(ReservationOwnerDialogsContext);
}

type ProviderProps = {
  children: ReactNode;
  reservation: ReservationGeneral | null;
  methods?: UseFormReturn<EditReservationFormI, unknown>;
  /**
   * Called when opening “Change dates” instead of `handleClosePopover`.
   * Use on surfaces (e.g. calendar) where `handleClosePopover` clears the selected
   * reservation/event and would unmount owner dialogs that need that reservation.
   */
  closePopoverKeepReservationSelection?: () => void;
  /**
   * Set synchronously before cancel/edit opens a dialog, so a calendar details popover
   * closing in the same turn does not clear the selected reservation and unmount the dialog.
   */
  retainSelectionRef?: { current: boolean };
} & PopoverState;

export function ReservationOwnerDialogsProvider({
  children,
  reservation,
  methods: externalMethods,
  closePopoverKeepReservationSelection,
  retainSelectionRef,
  ...popoverState
}: ProviderProps) {
  const {
    handleClosePopover,
    setSelectedRow,
    confirmAction,
    openConfirm,
    openEditDatePicker,
    setConfirmAction,
    setOpenConfirm,
    setOpenEditDatePicker,
    handleOpenCancelConfirmed
  } = popoverState;

  const defaultMethods = useForm<EditReservationFormI>();
  const methods = externalMethods || defaultMethods;
  const { reset } = methods;

  const [sendReservationModifyPut] = usePutRTKQReservationUpdateMutation();
  const [sendReservationCancelPut] = usePutRTKQReservationCancelMutation();
  const dispatch = useDispatch();

  const [confirmButtonLoading, setConfirmButtonLoading] = useState(false);
  /** DEV-2521: server explanation for a failed cancellation (403/404/409), shown in place. */
  const [cancelError, setCancelError] = useState<string | null>(null);
  const isMaintenanceBlock = isMaintenanceBlockReservation(reservation);
  const [originalDates, setOriginalDates] = useState<{
    check_in: Date | null;
    check_out: Date | null;
  }>({ check_in: null, check_out: null });

  useEffect(() => {
    if (reservation) {
      const originalCheckIn = reservation.check_in_ISO;
      const originalCheckOut = reservation.check_out_ISO;
      setOriginalDates({ check_in: originalCheckIn, check_out: originalCheckOut });
      reset({
        id: reservation.id,
        guest_id: reservation.guest_id,
        listing_id: reservation.listing_id,
        check_in: originalCheckIn,
        check_out: originalCheckOut
      });
    }
  }, [reservation, reset]);

  const { user } = useAuthContext();

  const confirmDialogTitle = useMemo(() => {
    switch (confirmAction) {
      case 'cancel':
        return isMaintenanceBlock ? 'Cancel maintenance block' : 'Cancel Booking';
      case 'edit':
        return 'Change Dates';
      case 'edit_confirm':
        return 'Change Dates';
      default:
        return 'Are you sure?';
    }
  }, [confirmAction, isMaintenanceBlock]);

  const confirmDialogContent = useMemo(() => {
    switch (confirmAction) {
      case 'cancel':
        if (isMaintenanceBlock && reservation) {
          const zone = reservation.timezone ?? brandConfig.timezone;
          const start = formatInstantInZone(reservation.check_in_ISO, zone);
          const end = formatInstantInZone(reservation.check_out_ISO, zone);
          return `Cancel the ${maintenanceBlockLabel(reservation.type)} block at ${
            reservation.name_detailed
          } from ${start} to ${end}? This cannot be undone. To use different times, create a new block.`;
        }
        return 'Are you sure you would like to cancel this booking?';
      case 'edit_confirm':
        return 'Are you sure you would like to change the dates of this booking?';
      default:
        return 'Are you sure?';
    }
  }, [confirmAction, isMaintenanceBlock, reservation]);

  const handleOpenConfirm = useCallback(
    (action: confirmActionT) => {
      // The row menu closes in the same click and that close calls handleCloseConfirm,
      // which would clear this dialog before it paints. Open on the next turn, after
      // the menu has finished dismissing.
      window.setTimeout(() => {
        if (retainSelectionRef) retainSelectionRef.current = true;
        setConfirmAction(action);
        if (action !== 'edit') {
          setOpenConfirm(true);
          return;
        }
        if (reservation) {
          const originalCheckIn = reservation.check_in_ISO;
          const originalCheckOut = reservation.check_out_ISO;
          setOriginalDates({ check_in: originalCheckIn, check_out: originalCheckOut });

          const isFutureReservation = moment(reservation.check_in_ISO).isAfter(moment(), 'day');

          if (isFutureReservation) {
            reset({
              id: reservation.id,
              guest_id: reservation.guest_id,
              listing_id: reservation.listing_id,
              check_in: undefined,
              check_out: undefined
            });
          } else {
            reset({
              id: reservation.id,
              guest_id: reservation.guest_id,
              listing_id: reservation.listing_id,
              check_in: originalCheckIn,
              check_out: originalCheckOut
            });
          }
        }
        setOpenEditDatePicker(true);
        (closePopoverKeepReservationSelection ?? handleClosePopover)();
      }, 0);
    },
    [
      reservation,
      reset,
      setConfirmAction,
      setOpenConfirm,
      setOpenEditDatePicker,
      handleClosePopover,
      closePopoverKeepReservationSelection,
      retainSelectionRef
    ]
  );

  const handleCloseConfirm = useCallback(() => {
    setCancelError(null);
    setOpenEditDatePicker(false);
    setOpenConfirm(false);
    setConfirmAction(null);
    setSelectedRow(null);
    handleClosePopover();
  }, [setOpenEditDatePicker, setOpenConfirm, setConfirmAction, setSelectedRow, handleClosePopover]);

  const handleCloseDatePicker = useCallback(() => {
    if (reservation) {
      const originalCheckIn = reservation.check_in_ISO;
      const originalCheckOut = reservation.check_out_ISO;
      setOriginalDates({ check_in: originalCheckIn, check_out: originalCheckOut });
      reset({
        id: reservation.id,
        guest_id: reservation.guest_id,
        listing_id: reservation.listing_id,
        check_in: originalCheckIn,
        check_out: originalCheckOut
      });
    }
    setOpenEditDatePicker(false);
    handleClosePopover();
  }, [reservation, reset, setOpenEditDatePicker, handleClosePopover]);

  const handleCancel = methods.handleSubmit(async (data) => {
    if (!user || !data.id) {
      return;
    }

    setCancelError(null);
    const result = await sendReservationCancelPut({ reservationID: data.id });

    if ('error' in result) {
      // DEV-2521: the server rechecks authorization/cutoff on submission. Show its
      // explanation (403/404/409) and refresh the record instead of reporting success.
      const classified = classifyBookingError(result.error);
      setCancelError(classified.message);
      if (classified.shouldRefresh) {
        dispatch(invalidateReservationCaches());
      }
      return;
    }

    handleCloseConfirm();
  });

  const handleEditDates = methods.handleSubmit(async (data) => {
    if (!user || !data.check_in || !data.check_out || !data.id) {
      return;
    }

    await sendReservationModifyPut({
      id: data.id,
      check_in: data.check_in,
      check_out: data.check_out
    });

    handleCloseConfirm();
  });

  const values = methods.watch();

  const datesHaveBeenModified = useMemo(() => {
    if (
      !originalDates.check_in ||
      !originalDates.check_out ||
      !values.check_in ||
      !values.check_out
    ) {
      return false;
    }

    const originalCheckIn = new Date(originalDates.check_in).toDateString();
    const originalCheckOut = new Date(originalDates.check_out).toDateString();
    const currentCheckIn = new Date(values.check_in).toDateString();
    const currentCheckOut = new Date(values.check_out).toDateString();

    return originalCheckIn !== currentCheckIn || originalCheckOut !== currentCheckOut;
  }, [originalDates, values.check_in, values.check_out]);

  const openCancelConfirmed = useCallback(() => {
    // Same turn as the details-popover click: let that popover finish closing before
    // the confirmation mounts, or its dismiss is treated as an outside click.
    window.setTimeout(() => {
      if (retainSelectionRef) retainSelectionRef.current = true;
      handleOpenCancelConfirmed();
    }, 0);
  }, [handleOpenCancelConfirmed, retainSelectionRef]);

  const contextValue = useMemo(
    () => ({
      handleOpenConfirm,
      handleCloseConfirm,
      handleOpenCancelConfirmed: openCancelConfirmed
    }),
    [handleOpenConfirm, handleCloseConfirm, openCancelConfirmed]
  );

  return (
    <ReservationOwnerDialogsContext.Provider value={contextValue}>
      {children}
      {reservation && (
        <>
          <Dialog open={openConfirm} onOpenChange={(open) => !open && handleCloseConfirm()}>
            <DialogContent className="flex h-dvh max-h-dvh flex-col overflow-hidden p-4 sm:h-auto sm:max-h-[90dvh] sm:max-w-lg sm:rounded-lg sm:p-6">
              <DialogHeader>
                <DialogTitle>{confirmDialogTitle}</DialogTitle>
                <DialogDescription>{confirmDialogContent}</DialogDescription>
              </DialogHeader>
              {cancelError && (
                <p className="text-sm text-destructive" role="alert">
                  {cancelError}
                </p>
              )}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-auto pt-4">
                <Button variant="outline" onClick={handleCloseConfirm}>
                  Cancel
                </Button>
                <FormProvider methods={methods} onSubmit={handleEditDates}>
                  <Button
                    loading={methods.formState.isSubmitting || confirmButtonLoading}
                    type={confirmAction === 'edit_confirm' ? 'submit' : 'button'}
                    variant="destructive"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setConfirmButtonLoading(true);
                      if (confirmAction === 'cancel') {
                        await handleCancel().catch((cancelError: unknown) => {
                          console.error(cancelError);
                        });
                      }
                      setConfirmButtonLoading(false);
                    }}
                  >
                    Confirm
                  </Button>
                </FormProvider>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={openEditDatePicker}
            onOpenChange={(open) => !open && handleCloseDatePicker()}
          >
            <DialogContent className="flex h-dvh max-h-dvh flex-col overflow-hidden p-4 sm:h-auto sm:max-h-none sm:overflow-visible sm:max-w-lg sm:min-w-[480px] sm:rounded-lg sm:p-6">
              <DialogHeader>
                <DialogTitle>{confirmDialogTitle}</DialogTitle>
              </DialogHeader>
              <div className="flex-1">
                <FormProvider methods={methods} onSubmit={handleEditDates}>
                  <DatePickerForm
                    listingId={reservation.listing_id}
                    ignoredOwnerReservation={reservation}
                    disablePortal
                  />
                </FormProvider>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-auto pt-4">
                <Button variant="outline" onClick={handleCloseDatePicker}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    values.check_in === null || values.check_out === null || !datesHaveBeenModified
                  }
                  onClick={() => {
                    handleOpenConfirm('edit_confirm');
                  }}
                >
                  Change Dates
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </ReservationOwnerDialogsContext.Provider>
  );
}
